"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import {
  analyticsEnabled,
  ANALYTICS_EVENTS,
  disableAnalytics,
  initAnalytics,
  track,
  type AnalyticsEvent,
} from "@/lib/analytics";
import { useConsent } from "@/lib/consent-context";
import { useIsEmbedded } from "@/lib/embedded";
import { regionFromPathname } from "@/lib/region";

export function AmplitudeAnalytics() {
  const { consent, hasDecided } = useConsent();
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const embedded = useIsEmbedded();
  // Never run inside the Storyblok visual editor, even if the preview
  // origin's localStorage already holds a consent decision.
  const allowed = hasDecided && consent.statistics && !embedded;

  // Init when statistics consent is granted; opt out if withdrawn.
  useEffect(() => {
    if (!analyticsEnabled()) return;
    if (allowed) initAnalytics({ region });
    else if (hasDecided) disableAnalytics();
  }, [allowed, hasDecided, region]);

  // Page views come from Amplitude autocapture ("[Amplitude] Page Viewed"),
  // which also keeps the page title in the event. The init effect above
  // re-runs on region change, refreshing the region user property.

  // Click tracking. Priority: explicit data-track-event, then automatic
  // detection of phone, outbound and navigation/footer link clicks.
  useEffect(() => {
    if (!analyticsEnabled() || embedded) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;

      // 1. Explicit: data-track-event + remaining data-track-* as props.
      const tracked = target?.closest?.("[data-track-event]");
      if (tracked) {
        const event = tracked.getAttribute("data-track-event");
        if (!event) return;
        const props: Record<string, string> = {};
        for (const attr of Array.from(tracked.attributes)) {
          if (attr.name.startsWith("data-track-") && attr.name !== "data-track-event") {
            props[attr.name.slice("data-track-".length)] = attr.value;
          }
        }
        track(event as AnalyticsEvent, props);
        return;
      }

      // 2. Automatic link detection.
      const a = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const label = (a.textContent || "").trim().slice(0, 80);
      const linkRegion = regionFromPathname(window.location.pathname);

      if (href.startsWith("tel:")) {
        track(ANALYTICS_EVENTS.PHONE_CLICKED, { href, region: linkRegion });
        return;
      }
      if (/^https?:\/\//i.test(href)) {
        let external = false;
        try {
          external = new URL(href).host !== window.location.host;
        } catch {}
        if (external) {
          track(ANALYTICS_EVENTS.OUTBOUND_LINK_CLICKED, { href, label });
          return;
        }
      }
      const navEl = a.closest("nav, footer, [role='navigation']");
      if (navEl) {
        track(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
          label,
          href,
          location:
            navEl.getAttribute("aria-label") || navEl.tagName.toLowerCase(),
          region: linkRegion,
        });
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [embedded]);

  return null;
}
